import { AfterViewInit, ChangeDetectorRef, Component, Input, OnInit, ViewChild } from '@angular/core';
import { IonInput, ModalController, Platform } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AppComponent } from '../../app.component';
import { RepositoryService } from '../../../smoothr-web-app-core/services/repository/repository.service';
import RepositoryDirective from '../../../smoothr-web-app-core/directives/repository-directive';
import Venue from '../../../smoothr-web-app-core/models/Venue';
import Address from '../../../smoothr-web-app-core/models/Address';
import { PreorderType } from '../../../smoothr-web-app-core/enums/PreorderType';
import { sleep, venueAcceptsOrders } from '../../../smoothr-web-app-core/utils/utils';
import { environment } from 'src/environments/environment';
import { AddressUtils } from '../../core/utils/address-utils';
import { AutocompleteService, AddressSuggestion } from '../../core/services/autocomplete.service';

@Component({
	selector: 'app-check-delivery-radius-modal',
	templateUrl: './check-delivery-radius-modal.component.html',
	styleUrls: ['check-delivery-radius-modal.component.scss'],
	standalone: false,
})
export class CheckDeliveryRadiusModalComponent extends RepositoryDirective implements OnInit, AfterViewInit {
	@ViewChild(IonInput, { static: true }) inputField: IonInput;
	@Input() selectedVenue: Venue;
	loading = false;
	venuesLoaded = false;
	searchTerm: string = '';
	address: Address;
	deliveryVenueAvailable = false;
	inputValue = '';
	allDeliveryVenues: Venue[] = [];
	predictions: AddressSuggestion[] = [];
	showStreetNumberError = false;

	constructor(
		protected repository: RepositoryService,
		private translate: TranslateService,
		private router: Router,
		private snackbarCtrl: MatSnackBar,
		private snackBar: MatSnackBar,
		private cdr: ChangeDetectorRef,
		private modalCtrl: ModalController,
		public platform: Platform,
		private autocompleteService: AutocompleteService
	) {
		super();
	}

	static async show(
		modalCtrl: ModalController,
		venue: Venue = null,
		address: Address = null
	): Promise<{ venue: Venue; address: Address; venues: Venue[]; action?: string }> {
		const modal = await modalCtrl.create({
			component: CheckDeliveryRadiusModalComponent,
			cssClass: 'delivery-modal',
			componentProps: {
				selectedVenue: venue,
				address: address,
			},
		});
		await modal.present();
		const result = await modal.onDidDismiss();
		await sleep(100);
		return result.data;
	}

	async ngOnInit() {
		// No maps initialization needed (handled by service)
	}

	largeScreen(): boolean {
		return AppComponent.largeScreen;
	}

	async resetSearch() {
		// Clear input and state
		if (this.inputField) {
			this.inputField.value = '';
		}
		this.inputValue = '';
		this.searchTerm = '';
		this.venuesLoaded = false;
		this.deliveryVenueAvailable = false;
		this.address = null;
		this.predictions = [];
		this.showStreetNumberError = false;

		this.cdr.detectChanges();
	}

	async loadPlace(prediction: AddressSuggestion) {
		this.loading = true;
		this.predictions = [];
		this.inputValue = prediction.description;
		this.showStreetNumberError = false;
		this.cdr.detectChanges();

		try {
			const placeDetails = await this.autocompleteService.getPlaceDetails(prediction.placeId);
			const address = this.autocompleteService.convertPlaceToAddress(placeDetails);

			if (!address.street || !address.number) {
				this.showStreetNumberError = true;
				this.predictions = [];
				return;
			}

			this.address = address;
			await this.loadVenues(address);
		} catch (e) {
			console.error('Error loading place details', e);
			this.snackBar.open('Fehler beim Laden der Adresse', null, { duration: 2000 });
			this.loading = false;
		}
	}

	async loadVenues(address: Address) {
		console.log('selectedVenue', this.selectedVenue);
		this.deliveryVenueAvailable = false;
		this.venuesLoaded = false;
		this.allDeliveryVenues = [];

		if (!address.postalCode) {
			this.address = null;
			this.loading = false;
			this.snackBar.open(this.translate.instant('home_page.street_error'), null, {
				duration: 2000,
			});
			this.cdr.detectChanges();
			return;
		}

		try {
			// Get venues by address (primarily postal code now)
			let allVenues = await this.repository.getVenuesByAddress(address);
			let foundVenue = null;
			if (this.selectedVenue) {
				foundVenue = allVenues.find(
					ven => ven._id === this.selectedVenue._id && venueAcceptsOrders(ven, PreorderType.DELIVERY)
				);
				this.deliveryVenueAvailable = foundVenue !== undefined;
			} else {
				const allVenuesFoundDelivery = allVenues.filter(ven => venueAcceptsOrders(ven, PreorderType.DELIVERY));
				this.allDeliveryVenues = allVenuesFoundDelivery;
				this.deliveryVenueAvailable = allVenuesFoundDelivery.length > 0;
			}
			this.venuesLoaded = true;
		} catch (e) {
			console.error(e);
			this.venuesLoaded = false;
		}
		this.loading = false;
		this.cdr.detectChanges();
	}

	async executeSearch() {
		if (!this.inputValue || this.inputValue.trim().length === 0) {
			return;
		}

		// Fallback to manual parsing if enter is pressed without selection
		this.loading = true;
		const parsedAddress = AddressUtils.parseAddressString(this.inputValue);

		if (!parsedAddress.postalCode) {
			// Maybe try to get the first prediction?
			if (this.predictions && this.predictions.length > 0) {
				await this.loadPlace(this.predictions[0]);
				return;
			}

			this.snackBar.open('Bitte geben Sie eine gültige Postleitzahl ein.', null, {
				duration: 2000,
			});
			this.loading = false;
			return;
		}

		this.address = parsedAddress;
		await this.loadVenues(parsedAddress);
	}

	async dismiss(address: Address) {
		this.modalCtrl.dismiss({
			selectedVenue: this.selectedVenue,
			address,
		});
	}

	async ngAfterViewInit() {
		if (this.inputField) {
			const inputElement = await this.inputField.getInputElement();
			inputElement.focus();
		}
	}

	async delivery() {
		if (this.selectedVenue) {
			await this.modalCtrl.dismiss({
				venue: this.selectedVenue,
				address: this.address,
			});
		} else {
			if (this.allDeliveryVenues.length > 0) {
				await this.modalCtrl.dismiss({
					venue: this.allDeliveryVenues[0],
					address: this.address,
				});
			} else {
				await this.modalCtrl.dismiss({
					address: this.address,
					venues: this.allDeliveryVenues,
				});
			}
		}
	}

	async selectPickup() {
		await this.modalCtrl.dismiss({
			action: 'pickup',
		});
	}

	async close() {
		await this.modalCtrl.dismiss();
	}

	async changeValue(event: any) {
		this.inputValue = event.detail.value;
		const query = this.inputValue;

		if (query && query.length > 2) {
			this.loading = true; // Optional: show loading spinner
			this.predictions = await this.autocompleteService.getAddressSuggestions(query);
			this.loading = false;
			this.cdr.detectChanges();
		} else {
			this.predictions = [];
		}
	}

	getAddressComponent(place: google.maps.places.PlaceResult, type: string) {
		return place.address_components?.find(c => c.types.includes(type));
	}
}
